import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Title,
  Group,
  Container,
  Avatar,
  Text, AspectRatio, Skeleton,
  Grid, createStyles, Divider
} from "@mantine/core";
import { useNavigate, useParams } from "react-router-dom";
import { searchFriend } from "../api/api-users"
import { SkeletonFriends } from "../components/molecules/SkeletonFriends"
import { secondsToDate, sortMoviesBy } from "../utils/utils"
import { Movie } from "../utils/types"
import { CardMovieCarousel } from "../components/organisms/CardMovieCarousel"
import { CardMovieList } from "../components/organisms/CardMovieList"
import { getMovie } from "../api/api-movies"
import { Spacer } from "../components/atoms/Spacer"
import { BtnHandleFriend } from "../components/molecules/BtnHandleFriend"
import { useAppSelector, useInfiniteScroll } from "../utils/hooks"
import Moment from "react-moment"
import { BtnHandleFriendFavourite } from "../components/molecules/BtnHandleFriendFavourite"
import { SimpleGrid } from "@mantine/core";
import ls from 'localstorage-slim';

const useStyles = createStyles((theme) => ({
  friendBarRt: {
    textAlign: 'right',

    [theme.fn.smallerThan('xs')]: {
      textAlign: 'left',
    },
  },
}));

export function FriendDetail() {

  const ITEMS_PER_PAGE = 12

  let params = useParams();
  const storageKey = `frienddetail_scroll_${params.username || 'default'}`
  const scrollYRef = useRef(0);
  const pendingScrollRef = useRef<number | null>(null);
  const { classes } = useStyles();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMoreWatched, setIsLoadingMoreWatched] = useState(false)

  const [user, setUser] = useState<any | null>(null)
  const [watchList, setWatchList] = useState<Movie[] | null>(null)
  const [watchedList, setWatchedList] = useState<Movie[] | null>(null)

  // Contatori totali (non paginati) da mostrare nell'intestazione
  const [totalWatchlistCount, setTotalWatchlistCount] = useState(0)
  const [totalWatchedCount, setTotalWatchedCount] = useState(0)

  const userState = useAppSelector((state) => state.user)
  const friendListStore = useAppSelector(state => state.friendList.friendList)

  // Aggiorna il ref ad ogni scroll (ref non ha problemi di closure)
  useEffect(() => {
    const handleScroll = () => {
      scrollYRef.current = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Listener sulla lista amici, se l'amico non è più in lista devo uscire dall'area
    if (user && friendListStore) {
      // Cerco se nella mia lista amcii è presente l'utente
      const findUserInFriendlist = friendListStore.find(friend => friend.docId === user.data.docId);

      // Se l'amico è in lista, controllo se la richiesta è in pending
      if (!findUserInFriendlist || findUserInFriendlist.inPending) {
        // Porto fuori l'utente
        navigate('/friends')
      }
    }
  }, [friendListStore])

  // useEffect separato per fetchare i dettagli utente da Firestore (solo al mount)
  useEffect(() => {
    const fetchUserData = async () => {
      if (params.username) {
        const fetchedUser = await searchFriend(params.username)

        // Se è il proprio profilo, faccio redirect alla watchlist
        if (userState.data.docId === fetchedUser.data.docId) {
          navigate('/watchlist');
          return;
        }
        // Se l'utente non è un amico, faccio redirect alla watchlist
        if (!fetchedUser.friendList.includes(userState.data.docId)) {
          navigate('/friends');
          return;
        }

        setUser(fetchedUser);
      }
    }

    fetchUserData();
  }, [])

  // useEffect per fetchare i dettagli dei film TMDB (dipende da user)
  useEffect(() => {
    if (!user) return;

    const fetchMovieDetails = async () => {
      setIsLoading(true);

      try {
        // === Watchlist (carousel)  ===
        const userWatchlistFiltered = user.movieList.filter((movie: any) => !movie.watched);
        const userWatchlistSorted = sortMoviesBy(userWatchlistFiltered, 'addedOn', 'desc');
        setTotalWatchlistCount(userWatchlistFiltered.length);

        // Slice PRIMA di fetchare: prendo solo quelli che mi servono per il carousel
        const watchlistToFetch = userWatchlistSorted.slice(0, ITEMS_PER_PAGE);
        const userWatchlist = await Promise.all(
          watchlistToFetch
            .map(async (movie: any) => {
              return await getMovie(parseInt(movie.id))
            })
        );
        setWatchList(userWatchlist);

        // === Film già visti (infinite scroll) ===
        const userWatchedlistFiltered = user.movieList.filter((movie: any) => movie.watched);
        const userWatchedlistSorted = sortMoviesBy(userWatchedlistFiltered, 'watchedOn', 'desc');
        setTotalWatchedCount(userWatchedlistFiltered.length);

        // Controlla se c'è uno stato salvato per ripristinare lo scroll
        const saved: any = ls.get(storageKey);
        let initialLoadCount = ITEMS_PER_PAGE;

        if (saved && saved.totalCount === userWatchedlistFiltered.length) {
          initialLoadCount = Math.max(saved.loadedCount, ITEMS_PER_PAGE);
          pendingScrollRef.current = saved.scrollY;
        }
        ls.remove(storageKey);

        // Slice PRIMA di fetchare: prendo solo quelli che mi servono
        const watchedToFetch = userWatchedlistSorted.slice(0, initialLoadCount);

        const userWatchedlist = await Promise.all(
          watchedToFetch
            .map(async (movie: any) => {
              const movieDetail = await getMovie(parseInt(movie.id))
              return {
                ...movieDetail,
                friendDetails: {
                  ...movie,
                  friendName: user.data.name
                }
              }
            })
        );
        setWatchedList(userWatchedlist);

      } catch (error) {
        console.error("Errore nel caricamento dei film:", error);
      } finally {
        setIsLoading(false)
      }
    }

    fetchMovieDetails();
  }, [user])

  // Caricamento di altri film visti (infinite scroll)
  const loadMoreWatched = useCallback(async () => {
    if (!user || !user.movieList || isLoadingMoreWatched) return;

    setIsLoadingMoreWatched(true);
    try {
      const userWatchedlistFiltered = user.movieList.filter((movie: any) => movie.watched);
      const userWatchedlistSorted = sortMoviesBy(userWatchedlistFiltered, 'watchedOn', 'desc');

      const startIndex = watchedList?.length || 0;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      const watchedToFetch = userWatchedlistSorted.slice(startIndex, endIndex);

      if (watchedToFetch.length === 0) {
        setIsLoadingMoreWatched(false);
        return;
      }

      const newWatchedMovies = await Promise.all(
        watchedToFetch.map(async (movie: any) => {
          const movieDetail = await getMovie(parseInt(movie.id));
          return {
            ...movieDetail,
            friendDetails: {
              ...movie,
              friendName: user.data.name
            }
          };
        })
      );

      setWatchedList(prev => [...(prev || []), ...newWatchedMovies]);
    } catch (error) {
      console.error("Errore nel caricamento dei film:", error);
    } finally {
      setIsLoadingMoreWatched(false);
    }
  }, [user, watchedList, isLoadingMoreWatched]);

  const hasMoreWatched = watchedList ? watchedList.length < totalWatchedCount : false;

  const sentinelRef = useInfiniteScroll(loadMoreWatched, hasMoreWatched && !isLoadingMoreWatched);

  // Ripristina lo scroll DOPO che React ha renderizzato i nuovi film nel DOM
  useEffect(() => {
    if (pendingScrollRef.current !== null && watchedList && watchedList.length > 0) {
      const scrollTo = pendingScrollRef.current;
      pendingScrollRef.current = null;
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollTo);
      });
    }
  }, [watchedList]);

  // Salva in localStorage all'unmount usando il ref (che ha il valore reale,
  // non quello resettato da ScrollToTop)
  useEffect(() => {
    return () => {
      if (watchedList && watchedList.length > 0) {
        ls.set(storageKey, {
          loadedCount: watchedList.length,
          scrollY: scrollYRef.current,
          totalCount: totalWatchedCount,
        });
      }
    };
  }, [watchedList, totalWatchedCount]);

  return (
    <>
      {
        (user && watchList && watchedList && !isLoading) ?
          <>
            <Container>
              <Group position="apart" align="center">
                <Group>
                  <Avatar
                    src={user.data.avatar}
                    radius={100}
                    size={100}
                  />
                  <div>
                    <Group mb="xs">
                      <Title order={1}>{user.data.name}</Title>
                      <BtnHandleFriendFavourite
                        userFrom={userState.data.docId}
                        userTo={user.data.docId}
                      />
                    </Group>
                    <BtnHandleFriend
                      size="xs"
                      userFrom={userState.data.docId}
                      userTo={user.data.docId}
                    />
                  </div>
                </Group>
                <div className={classes.friendBarRt}>
                  <Text size="sm" color="dimmed">{totalWatchlistCount} film nella watchlist</Text>
                  <Text size="sm" color="dimmed">{totalWatchedCount} film segnati come visti</Text>
                  <Text size="sm">Utente
                    da <Moment fromNow ago>{secondsToDate(user.data.signUpOn.seconds)}</Moment></Text>
                </div>
              </Group>
            </Container>

            <Spacer smallerXs={80} largerXs={50}/>

            {
              (watchList.length > 0) &&
              <>
                <Container>
                  <Group position="apart" align="center" mb="xl">
                    <Title order={4}>Gli ultimi dalla watchlist di {user.data.name}</Title>
                  </Group>
                </Container>

                <CardMovieCarousel
                  id={1}
                  movieList={watchList}
                  isLoading={isLoading}
                />

                <Spacer smallerXs={80} largerXs={50}/>
              </>
            }

            {
              watchedList.length > 0 &&
              <>

								<Container mb="xl">
                  <Group position="apart" align="center" mb="xl">
                    <Title order={4}>Tutti i film visti da {user.data.name}</Title>
                  </Group>
                  <Divider my="sm" variant="dotted"/>
                  <Group position="apart" align="center">
                    <Text size="sm">{totalWatchedCount} film</Text>
                  </Group>
                </Container>

                <CardMovieList
                  movieList={watchedList}
                  isLoading={false}
                  mobileView="compact"
                />

                {/* Scheletri per il caricamento di più film visti */}
                {isLoadingMoreWatched && (
                  <Container mt="md">
                    <SimpleGrid
                      cols={4}
                      breakpoints={[
                        { maxWidth: 768, cols: 2 },
                        { maxWidth: 600, cols: 2 },
                      ]}
                    >
                      {
                        [...Array(4)].map((item: number, i: number) => (
                          <Skeleton key={i} style={{aspectRatio: 2/3}}/>
                        ))
                      }
                    </SimpleGrid>
                  </Container>
                )}

                {/* Sentinel per l'infinite scroll */}
                {hasMoreWatched && <div ref={sentinelRef} style={{ height: 1 }}/>}

                <Spacer smallerXs={80} largerXs={50}/>
              </>
            }

          </>
          :
          <Container>

            <SkeletonFriends/>
            <Spacer smallerXs={50} largerXs={20}/>

            {
              [...Array(2)].map((item: number, i: number) => (
                <Grid mb={50} key={i}>
                  {
                    [...Array(3)].map((item: number, ic: number) => (
                      <Grid.Col key={ic} xs={12} md={4}>
                        <Skeleton height={500}/>
                      </Grid.Col>
                    ))
                  }
                </Grid>
              ))
            }

          </Container>
      }
    </>
  )

}
